"use client"

import { useSyncExternalStore, type ReactNode } from "react"
import { createPortal } from "react-dom"

export const PAGE_HEADER_SLOT_ID = "page-header-slot"
export const PAGE_FOOTER_SLOT_ID = "page-footer-slot"

/**
 * The two places a page can contribute controls to the app shell: the header, and the footer.
 *
 * A portal rather than props, because the layout renders one shell for every route and cannot
 * know which page is below it — Template's `topMenu` prop was passed the same value by every
 * call site for exactly that reason, so no page could use it. The shell renders empty slots and
 * whichever page is mounted fills them; controls unmount with the page, so nothing cleans up.
 *
 * A portal rather than a context holding a ReactNode, because that would mean a child setting
 * parent state during render — the cascading-render pattern React's own lint rule rejects, and
 * which has bitten this page before.
 *
 * The footer is the one difference between the ends: the header is always there, while the
 * footer only EXISTS when a page puts something in it. Nothing here decides that — the shell
 * hides an empty footer with `:empty` and lays the page out around it with `:has()`, so the
 * appearing and the fade beneath it are both free (see AppFooter and AppShell).
 */
// The slots are DOM nodes the shell owns — an external store as far as React is concerned.
// Reading them with useSyncExternalStore rather than an effect keeps this out of the
// cascading-render pattern (setState inside useEffect) that React's lint rule rejects, and
// gives a correct SSR/hydration story for free: the server snapshot is null, and the client
// re-reads once hydrated. `subscribe` is a no-op because the slots are rendered by the layout
// above this component and never come and go while a page is mounted.
const subscribe = () => () => {}
const getServerSnapshot = (): HTMLElement | null => null

function useSlot(id: string) {
  return useSyncExternalStore(
    subscribe,
    // Recreated per id rather than shared, so the two slots do not read each other's node.
    () => document.getElementById(id),
    getServerSnapshot,
  )
}

function Slot({ id, children }: { id: string; children: ReactNode }) {
  const target = useSlot(id)
  if (!target) return null
  return createPortal(children, target)
}

/** Renders its children into the app header. */
export function PageHeaderSlot({ children }: { children: ReactNode }) {
  return <Slot id={PAGE_HEADER_SLOT_ID}>{children}</Slot>
}

/**
 * Renders its children into the app footer — and, by filling it, is what makes the footer
 * exist at all. Render nothing here and there is no footer and no space reserved for one.
 */
export function PageFooterSlot({ children }: { children: ReactNode }) {
  return <Slot id={PAGE_FOOTER_SLOT_ID}>{children}</Slot>
}
